import { computed, onMounted, ref, watch } from 'vue'
import { DateTime } from 'luxon'
import api from '@/services/api'
import { type LiveOpsCurrency, type LiveOpsEventTemplate, type LiveOpsUserEvent } from '@/types/live-ops'
import { useNow } from '@/composables/game/time'
import { syncPlayer } from '@/composables/game/player'

export type LiveOpsEventWithTemplate = LiveOpsUserEvent & {
  template: LiveOpsEventTemplate
}

export const initialLiveOpsEvents = ref<LiveOpsEventWithTemplate[]>([])
export const liveOpsCurrencies = ref<LiveOpsCurrency[]>([])
export const liveOpsEvents = ref<LiveOpsEventWithTemplate[]>([])
export const liveOpsEventTemplates = ref<LiveOpsEventTemplate[]>([])

export async function fetchInitialLiveOpsEvent() {
  try {
    const response = await api.liveOpsGetUserEvents()
    const events: LiveOpsEventWithTemplate[] = response.userEvents.map(userEvent => {
      const template = response.eventTemplates.find(t => t.id === userEvent.eventTemplateId)
      return { ...userEvent, template: template! }
    }).filter(e => e.template)

    liveOpsEvents.value = events
    const now = DateTime.utc().toUnixInteger()
    initialLiveOpsEvents.value = events
      .filter(event =>
        event.endTimestamp > now && !event.isComplete && event.template.cosmetic?.['popupOrder'] != null && !isNaN(Number(event.template.cosmetic['popupOrder'])),
      )
      .sort((a, b) => Number(a.template.cosmetic!['popupOrder']) - Number(b.template.cosmetic!['popupOrder']))
    liveOpsCurrencies.value = response.currencies
    liveOpsEventTemplates.value = response.eventTemplates
  } catch (e) {
    console.error('[LiveOpsEvents] Failed to fetch initial event:', e)
    initialLiveOpsEvents.value = []
    liveOpsCurrencies.value = []
  }
}

export async function fetchLiveOpsEvents() {
  try {
    const response = await api.liveOpsGetUserEvents()
    liveOpsEvents.value = response.userEvents.map(userEvent => {
      const template = response.eventTemplates.find(t => t.id === userEvent.eventTemplateId)
      return { ...userEvent, template: template! }
    }).filter(e => e.template)
  } catch (e) {
    console.error('[LiveOpsEvents] Failed to fetch:', e)
  }
}

export function useLiveOpsEvents() {
  const { now } = useNow()

  const activeLiveOpsEvents = computed(() =>
    liveOpsEvents.value
      .filter(event => event.endTimestamp > now.value && !event.isComplete)
      .sort((a, b) => {
        const priority = (packageId: string) => {
          if (packageId === 'progressive_lane') return 12
          if (packageId === 'special_wheel') return 11
          if (packageId === 'slot_machine') return 10
          if (packageId === 'plinko') return 9
          if (packageId === 'pixel_art') return 8
          if (packageId === 'lucky_pick') return 7
          if (packageId === 'stamp_card') return 6
          if (packageId === 'winding_path') return 5
          if (packageId === 'pathway') return 4
          if (packageId === 'badge_room') return 3
          if (packageId === 'sale_path') return 2
          if (packageId === 'bogo') return 1
          return 0
        }
        return priority(b.template.packageId ?? '') - priority(a.template.packageId ?? '')
      }),
  )

  watch(
    () => liveOpsEvents.value.some(e => e.endTimestamp <= now.value),
    (hasExpiredEvent) => {
      if (hasExpiredEvent) {
        setTimeout(async () => {
          await fetchLiveOpsEvents()
          await syncPlayer()
        }, 1000)
      }
    },
  )

  onMounted(fetchLiveOpsEvents)

  return { liveOpsEvents: activeLiveOpsEvents, fetchLiveOpsEvents }
}
